import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  CheckCircle2,
  Building2,
  Lock,
  Mail,
  Download,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ZoomIn } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import WorkbookOrderModal from "@/components/WorkbookOrderModal";
import preview1 from "@/assets/workbook-preview-1.webp";
import preview2 from "@/assets/workbook-preview-2.webp";
import coverImg from "@/assets/workbook-cover.webp";

const PRICE_NET = "499,00 €";
const PRICE_GROSS = "593,81 €";

const includes = [
  "Strukturierter Leitfaden zu OT-Security in der Gebäudeautomation",
  "Self-Assessment zur Standortbestimmung Ihrer Liegenschaft",
  "Konkrete Handlungsfelder mit Priorisierung (Quick Wins & Roadmap)",
  "NIS-2 Mapping: Welche Anforderungen treffen GA-Betreiber wirklich?",
  "Checklisten für Betreiber, Planer und Integratoren",
  "Vorlagen für interne Abstimmung mit IT, Facility und Geschäftsleitung",
  "60 Min. Online Assessment Sparring mit Tim Bauer (im Preis inkludiert)",
];

const audience = [
  {
    title: "Betreiber & Eigentümer",
    text: "Sie verantworten Liegenschaften und wollen wissen, wo Sie bei OT-Security wirklich stehen.",
  },
  {
    title: "Facility & Technisches Gebäudemanagement",
    text: "Sie betreiben GA-Systeme und brauchen einen klaren Fahrplan zwischen Tagesgeschäft und NIS-2.",
  },
  {
    title: "Planer, Integratoren, Errichter",
    text: "Sie planen oder integrieren GA-Anlagen und wollen Security strukturiert in Projekte einbringen.",
  },
  {
    title: "IT- & Security-Verantwortliche",
    text: "Sie verstehen IT-Security, brauchen aber den Brückenschlag in die OT- und GA-Welt.",
  },
];

const faqs = [
  {
    q: "Was bekomme ich konkret?",
    a: "Sie erhalten ein PDF Workbook mit Leitfaden, Self-Assessment, Checklisten und Vorlagen rund um OT-Security in der Gebäudeautomation, inklusive NIS-2 Bezug.",
  },
  {
    q: "Wie erfolgt die Zustellung?",
    a: "Sie erhalten zunächst eine Bestellbestätigung per E-Mail und kurz darauf die Rechnung. Nach Zahlungseingang sende ich Ihnen das Workbook als PDF per E-Mail zu.",
  },
  {
    q: "Ist die Umsatzsteuer enthalten?",
    a: "Der Preis beträgt 499,00 € netto, zzgl. 19% deutscher Umsatzsteuer (94,81 €), brutto 593,81 €. Bei gültiger USt-IdNr. innerhalb der EU prüfe ich Reverse Charge.",
  },
  {
    q: "Wer kann bestellen?",
    a: "Das Workbook ist ein B2B-Angebot. Bestellungen sind ausschließlich für Unternehmen, Behörden und Selbstständige möglich.",
  },
  {
    q: "Welche Zahlungsmethoden gibt es?",
    a: "Aktuell erfolgt die Bestellung ausschließlich per Rechnung. Sie erhalten die Rechnung per E-Mail mit Bankverbindung und üblichen Zahlungszielen.",
  },
  {
    q: "Gibt es ein Widerrufsrecht?",
    a: "Als B2B-Angebot besteht kein gesetzliches Widerrufsrecht. Bei Fragen vor der Bestellung sprechen Sie mich gern direkt an.",
  },
];

const Workbook = () => {
  const [showSticky, setShowSticky] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [zoomImg, setZoomImg] = useState<{ src: string; alt: string } | null>(null);
  const handleBuy = () => setOrderOpen(true);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const openOrder = () => setOrderOpen(true);
    window.addEventListener("open-workbook-order", openOrder);
    return () => window.removeEventListener("open-workbook-order", openOrder);
  }, []);

  return (
    <>
      <Helmet>
        <title>OT-Security Workbook Gebäudeautomation | Fachsprechpartner</title>
        <meta
          name="description"
          content="OT-Security Workbook für die Gebäudeautomation: Self-Assessment, NIS-2 Mapping, Checklisten und 60 Min. Sparring. 499 € netto."
        />
        <link rel="canonical" href="https://fachsprechpartner.de/workbook" />
        <link rel="alternate" hrefLang="de" href="https://fachsprechpartner.de/workbook" />
        <link rel="alternate" hrefLang="x-default" href="https://fachsprechpartner.de/workbook" />
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://fachsprechpartner.de/workbook" />
        <meta property="og:title" content="OT-Security Workbook Gebäudeautomation | Fachsprechpartner" />
        <meta
          property="og:description"
          content="OT-Security Workbook für die Gebäudeautomation: Self-Assessment, NIS-2 Mapping, Checklisten und 60 Min. Sparring. 499 € netto."
        />
        <meta property="og:image" content="https://fachsprechpartner.de/workbook-og.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="OT-Security Workbook für Gebäudeautomation von Fachsprechpartner" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OT-Security Workbook Gebäudeautomation | Fachsprechpartner" />
        <meta
          name="twitter:description"
          content="OT-Security Workbook für die Gebäudeautomation: Self-Assessment, NIS-2 Mapping, Checklisten und 60 Min. Sparring. 499 € netto."
        />
        <meta name="twitter:image" content="https://fachsprechpartner.de/workbook-og.webp" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "OT-Security Workbook für Gebäudeautomation",
          description: "PDF Workbook mit Self-Assessment, NIS-2 Mapping und Checklisten zur OT-Security in der Gebäudeautomation. Inkl. 60 Min. Online Assessment Sparring.",
          image: "https://fachsprechpartner.de/workbook-og.webp",
          brand: { "@type": "Brand", name: "FACHSPRECHPARTNER" },
          offers: {
            "@type": "Offer",
            url: "https://fachsprechpartner.de/workbook",
            price: "499.00",
            priceCurrency: "EUR",
            priceSpecification: {
              "@type": "PriceSpecification",
              price: "499.00",
              priceCurrency: "EUR",
              valueAddedTaxIncluded: false,
            },
            availability: "https://schema.org/InStock",
            eligibleCustomerType: "https://schema.org/Business",
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
              shippingDestination: [
                { "@type": "DefinedRegion", addressCountry: "DE" },
                { "@type": "DefinedRegion", addressCountry: "AT" },
                { "@type": "DefinedRegion", addressCountry: "CH" },
              ],
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
              },
            },
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: ["DE", "AT", "CH"],
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
      </Helmet>
      <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 bg-gradient-to-br from-primary via-primary to-[hsl(var(--hero-overlay))] text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none [background-image:radial-gradient(circle_at_20%_20%,hsl(var(--secondary-bright))_0,transparent_45%),radial-gradient(circle_at_80%_60%,hsl(var(--accent))_0,transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Zurück zur Startseite
          </Link>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-bright/20 text-secondary-bright text-xs font-semibold uppercase tracking-wider mb-6">
                <ShieldCheck size={14} /> FACHSPRECHPARTNER Workbook
              </span>
              <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-5">
                OT-Security: Handlungsfelder in der Gebäudeautomation
              </h1>
              <p className="text-base md:text-lg text-primary-foreground/85 mb-8 leading-relaxed text-cyan-400">
                Mit dem FACHSPRECHPARTNER Workbook, inkl. Self-Assessment und 60 Min.
                Online Assessment, schnell in Handlung kommen und bereit für NIS-2 sein. Inklusive Mapping von NIS-2, IEC62443 und BSI Grundschutz Infrastrukturbausteine 13 und 14 (BSI INF.13 und INF.14)
              </p>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8 text-sm text-primary-foreground/80">
                <div className="flex items-center gap-2"><FileText size={16} /> PDF Workbook</div>
                <div className="flex items-center gap-2"><Lock size={16} /> B2B-Angebot</div>
                <div className="flex items-center gap-2"><Mail size={16} /> Zahlung per Rechnung</div>
                <div className="flex items-center gap-2"><Download size={16} /> Versand nach Zahlungseingang</div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="button"
                  onClick={handleBuy}
                  className="inline-flex items-center justify-center gap-2 bg-cta text-cta-foreground px-6 py-3.5 rounded-md font-bold text-sm uppercase tracking-wider shadow-lg shadow-cta/30 hover:bg-cta-hover hover:-translate-y-0.5 transition-all"
                >
                  Jetzt kaufen
                </button>
                <div className="text-xs text-primary-foreground/70 leading-snug">
                  499€ netto zzgl. 19% USt. ({PRICE_GROSS} brutto)
                  <br />
                  B2B · Zahlung per Rechnung
                </div>
              </div>
            </div>

            {/* Cover mock */}
            <div className="relative mx-auto w-full max-w-sm">
              <img
                src={coverImg}
                alt="Deckblatt FACHSPRECHPARTNER Workbook OT-Security in der Gebäudeautomation"
                className="w-full h-auto rounded-lg border border-primary-foreground/10 shadow-2xl shadow-black/40"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vorschau */}
      <section className="py-16 md:py-24 bg-section-alt">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
              Einblick
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold mt-2 text-foreground">
              Eine kleine Vorschau
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Sehen Sie, was Sie erwartet. Die vollständigen Inhalte erhalten Sie nach dem Kauf.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { label: "Inhaltsverzeichnis", n: 2, src: preview1 },
              { label: "IEC-62443-Arbeitslogik für das Workbook", n: 8, src: preview2 },
            ].map((p) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative"
              >
                <button
                  type="button"
                  onClick={() =>
                    setZoomImg({ src: p.src, alt: `${p.label} – Vorschau Seite ${p.n}` })
                  }
                  className="group relative block w-full rounded-md bg-card border border-border shadow-lg overflow-hidden cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label={`${p.label} vergrößern`}
                >
                  <img
                    src={p.src}
                    alt={`${p.label} – Vorschau Seite ${p.n}`}
                    loading="lazy"
                    className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  {/* Centered zoom indicator */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-background/70 backdrop-blur-sm rounded-full p-3 shadow-lg">
                      <ZoomIn size={28} className="text-foreground" />
                    </div>
                  </div>
                  {/* Top-right badge */}
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-background/80 backdrop-blur px-2 py-1 text-xs text-foreground shadow-sm">
                    <ZoomIn size={14} /> Zoom
                  </span>
                </button>
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  {p.label} · Seite {p.n}
                </div>
                {/* Wasserzeichen */}
                <div className="pointer-events-none absolute inset-x-0 top-0 bottom-8 flex items-center justify-center">
                  <div className="rotate-[-20deg] text-foreground/10 text-5xl font-display font-bold uppercase tracking-widest">
                    Vorschau
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Was du bekommst */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
              Inhalt
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold mt-2 text-foreground">
              Was Sie bekommen
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Ein praxisorientiertes Workbook, das Sie sofort einsetzen können, um Klarheit über den
              OT-Security Status Ihrer Gebäudeautomation zu gewinnen und konkrete Schritte einzuleiten.
              Inklusive 60 Minuten Online Assessment Sparring zur Ergebnisbesprechung.
            </p>
          </div>
          <ul className="space-y-3">
            {includes.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 p-4 rounded-md bg-card border border-border"
              >
                <CheckCircle2
                  size={20}
                  className="text-secondary mt-0.5 shrink-0"
                  strokeWidth={2.25}
                />
                <span className="text-sm text-foreground leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Für wen */}
      <section className="py-16 md:py-24 bg-section-alt">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
              Zielgruppe
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold mt-2 text-foreground">
              Für wen das Workbook gemacht ist
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {audience.map((a) => (
              <div
                key={a.title}
                className="p-6 rounded-md bg-card border border-border hover:border-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-md bg-secondary/10 text-secondary flex items-center justify-center">
                    <Building2 size={20} />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-foreground">{a.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
              FAQ
            </span>
            <h2 className="font-display text-2xl md:text-4xl font-bold mt-2 text-foreground">
              Häufige Fragen
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-border rounded-md bg-card px-4"
              >
                <AccordionTrigger className="text-left text-foreground font-medium hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 pb-28 md:pb-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-2xl md:text-4xl font-bold mb-4">
            Bereit, OT-Security in Ihrer GA strukturiert anzugehen?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Holen Sie sich das Workbook, starten Sie mit dem Self-Assessment und kommen Sie schnell
            in Handlung, bereit für NIS-2.
          </p>
          <button
            type="button"
            onClick={handleBuy}
            className="inline-flex items-center gap-2 bg-cta text-cta-foreground px-7 py-4 rounded-md font-bold text-sm uppercase tracking-wider shadow-lg shadow-cta/30 hover:bg-cta-hover hover:-translate-y-0.5 transition-all"
          >
            Workbook für {PRICE_NET} netto kaufen
          </button>
          <div className="text-xs text-primary-foreground/60 mt-4">
            zzgl. 19% USt. ({PRICE_GROSS} brutto) · Zahlung per Rechnung · Versand nach Bezahlung
          </div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div
        className={`fixed bottom-0 inset-x-0 z-40 md:hidden transition-transform duration-300 ${
          showSticky ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="bg-background/95 backdrop-blur-md border-t border-border px-4 py-3 flex items-center gap-3 shadow-2xl">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Workbook
            </div>
            <div className="font-bold text-foreground text-sm">{PRICE_NET} netto</div>
          </div>
          <button
            type="button"
            onClick={handleBuy}
            className="inline-flex items-center gap-1.5 bg-cta text-cta-foreground px-4 py-2.5 rounded-md font-bold text-xs uppercase tracking-wider shadow-md shadow-cta/30"
          >
            <ChevronDown size={14} className="rotate-180" /> Kaufen
          </button>
        </div>
      </div>

      <WorkbookOrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />
      <Dialog open={!!zoomImg} onOpenChange={(o) => !o && setZoomImg(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-2 sm:p-4 bg-background">
          {zoomImg && (
            <div className="relative max-h-[85vh] overflow-auto rounded-md">
              <img
                src={zoomImg.src}
                alt={zoomImg.alt}
                className="w-full h-auto block"
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="rotate-[-20deg] text-foreground/10 text-7xl font-display font-bold uppercase tracking-widest">
                  Vorschau
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  </>);
};

export default Workbook;